import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type ChatTab = 'analysts' | 'buyers';

interface ChatUiState {
  modalOpen: boolean;
  minimized: boolean;
  productId?: string;
  orderId?: number;
  tab: ChatTab;
  openModal: (arg?: string | { tab?: ChatTab; productId?: string; orderId?: number }) => void;
  closeModal: () => void;
  toggleMinimize: () => void;
  setProductId: (productId?: string) => void;
  setTab: (tab: ChatTab) => void;
  setOrderId: (orderId?: number) => void;
}

const ChatUiContext = createContext<ChatUiState | null>(null);

function parseOpenArg(arg?: string | { tab?: ChatTab; productId?: string; orderId?: number }) {
  if (arg && typeof arg === 'object') {
    return {
      tab: arg.tab === 'buyers' ? 'buyers' as const : 'analysts' as const,
      productId: arg.productId,
      orderId: arg.orderId
    };
  }
  return { tab: 'analysts' as const, productId: arg, orderId: undefined };
}

export function ChatUiProvider({ children }: { children: React.ReactNode }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [productId, setProductId] = useState<string | undefined>();
  const [orderId, setOrderId] = useState<number | undefined>();
  const [tab, setTab] = useState<ChatTab>('analysts');

  const openModal = useCallback((arg?: string | { tab?: ChatTab; productId?: string; orderId?: number }) => {
    const next = parseOpenArg(arg);
    setTab(next.tab);
    setProductId(next.productId);
    setOrderId(next.orderId);
    setMinimized(false);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setMinimized(false);
  }, []);

  const toggleMinimize = useCallback(() => {
    setMinimized((v) => !v);
  }, []);

  const value = useMemo(
    () => ({
      modalOpen,
      minimized,
      productId,
      orderId,
      tab,
      openModal,
      closeModal,
      toggleMinimize,
      setProductId,
      setTab,
      setOrderId,
    }),
    [modalOpen, minimized, productId, orderId, tab, openModal, closeModal, toggleMinimize]
  );

  return <ChatUiContext.Provider value={value}>{children}</ChatUiContext.Provider>;
}

export function useChatUi() {
  const ctx = useContext(ChatUiContext);
  if (!ctx) throw new Error('useChatUi must be used within ChatUiProvider');
  return ctx;
}
